import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { fadeInUp, scaleIn, defaultTransition } from '../utils/animations';

export default function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showResetForm, setShowResetForm] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (signInError) throw signInError;
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eroare la autentificare');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        formData.email,
        { redirectTo: `${window.location.origin}/schimba-parola` }
      );

      if (resetError) throw resetError;

      setSuccess('Email-ul pentru resetarea parolei a fost trimis!');
      setTimeout(() => setShowResetForm(false), 3000);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Eroare la trimiterea email-ului de resetare'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-[#024994] via-[#012a52] to-[#0A0E27] flex flex-col justify-center py-12 sm:px-6 lg:px-8 overflow-hidden">
      {/* Enhanced Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Orbs - More than About but less than Hero */}
        <motion.div
          className="absolute top-20 left-10 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl"
          animate={{
            y: [-15, 15, -15],
            x: [-10, 10, -10],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-80 h-80 bg-blue-500/15 rounded-full blur-3xl"
          animate={{
            y: [15, -15, 15],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/3 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.4, 0.6, 0.4],
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-1/3 left-1/4 w-72 h-72 bg-cyan-400/10 rounded-full blur-3xl"
          animate={{
            y: [-10, 10, -10],
            rotate: [0, 180, 0],
            transition: {
              duration: 15,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:50px_50px]" />

        {/* Particles - More than About */}
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400/40 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 0.8, 0],
              transition: {
                duration: 4 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
                ease: 'easeInOut',
              },
            }}
          />
        ))}
      </div>

      <motion.div
        className="relative sm:mx-auto sm:w-full sm:max-w-md"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={defaultTransition}
      >
        <motion.h2
          className="mt-6 text-center text-3xl font-extrabold text-white drop-shadow-lg"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ ...defaultTransition, delay: 0.1 }}
        >
          {showResetForm ? 'Resetare parolă' : 'Autentificare'}
        </motion.h2>
      </motion.div>

      <motion.div
        className="relative mt-8 sm:mx-auto sm:w-full sm:max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...defaultTransition, delay: 0.2 }}
      >
        <div
          className="bg-white/10 backdrop-blur-xl py-8 px-4 shadow-2xl sm:rounded-2xl sm:px-10 border border-white/20"
        >
          <AnimatePresence mode="wait">
            {showResetForm ? (
              <motion.form
                key="reset"
                className="space-y-6"
                onSubmit={handleResetPassword}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={defaultTransition}
              >
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-white"
                  >
                    Email
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="appearance-none block w-full px-3 py-2 bg-white/90 border border-white/30 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent focus:bg-white transition"
                    />
                  </div>
                </div>

                {error && (
                  <motion.div
                    className="text-red-300 text-sm bg-red-500/20 p-3 rounded-md border border-red-400/30"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {error}
                  </motion.div>
                )}

                {success && (
                  <motion.div
                    className="text-green-300 text-sm bg-green-500/20 p-3 rounded-md border border-green-400/30"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {success}
                  </motion.div>
                )}

                <div className="flex flex-col space-y-3">
                  <motion.button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-400 disabled:opacity-50 transition"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {loading ? 'Se procesează...' : 'Trimite email de resetare'}
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => setShowResetForm(false)}
                    className="w-full flex justify-center py-2 px-4 border border-white/30 rounded-md shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-400 transition"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Înapoi la autentificare
                  </motion.button>
                </div>
              </motion.form>
            ) : (
              <motion.form
                key="login"
                className="space-y-6"
                onSubmit={handleSubmit}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={defaultTransition}
              >
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-white"
                  >
                    Email
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="appearance-none block w-full px-3 py-2 bg-white/90 border border-white/30 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent focus:bg-white transition"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-white"
                  >
                    Parolă
                  </label>
                  <div className="mt-1">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="appearance-none block w-full px-3 py-2 bg-white/90 border border-white/30 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent focus:bg-white transition"
                    />
                  </div>
                </div>

                {error && (
                  <motion.div
                    className="text-red-300 text-sm bg-red-500/20 p-3 rounded-md border border-red-400/30"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {error}
                  </motion.div>
                )}

                <div className="flex flex-col space-y-3">
                  <motion.button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-lg text-sm font-medium text-white bg-gradient-to-r from-[#024994] to-[#012a52] hover:from-[#012a52] hover:to-[#024994] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-400 disabled:opacity-50 transition"
                    whileHover={{
                      scale: 1.02,
                      boxShadow: '0 0 20px rgba(0, 217, 255, 0.5)',
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {loading ? 'Se procesează...' : 'Autentificare'}
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => setShowResetForm(true)}
                    className="text-cyan-300 hover:text-cyan-100 text-sm font-medium transition"
                    whileHover={{ scale: 1.05 }}
                  >
                    Am uitat parola
                  </motion.button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
